import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useStockholmEventTracking } from "../lib/analytics";

export type StockholmJourney = "ansoka" | "valja" | "byta" | "narstaende";

const JOURNEY_STORAGE_KEY = "hemtjanstistockholm:journey:v1";
const VALID_JOURNEYS = new Set<StockholmJourney>([
  "ansoka",
  "valja",
  "byta",
  "narstaende",
]);

function safeJourney(value: string | null): StockholmJourney | null {
  return value && VALID_JOURNEYS.has(value as StockholmJourney)
    ? (value as StockholmJourney)
    : null;
}

function readStoredJourney(): StockholmJourney | null {
  if (typeof window === "undefined") return null;
  try {
    return safeJourney(window.localStorage.getItem(JOURNEY_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persistJourney(journey: StockholmJourney): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(JOURNEY_STORAGE_KEY, journey);
  } catch {
    // Journey selection remains available in React state when storage is
    // blocked by browser privacy settings.
  }
}

interface JourneyContextValue {
  journey: StockholmJourney | null;
  selectJourney: (journey: StockholmJourney) => void;
  hrefFor: (path: string) => string;
}

const JourneyContext = createContext<JourneyContextValue | null>(null);

export function JourneyProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const trackStockholmEvent = useStockholmEventTracking();
  const fromUrl = safeJourney(new URLSearchParams(location.search).get("resa"));
  const [journey, setJourney] = useState<StockholmJourney | null>(fromUrl);

  useEffect(() => {
    if (fromUrl) {
      setJourney(fromUrl);
      persistJourney(fromUrl);
      return;
    }
    const stored = readStoredJourney();
    if (stored) setJourney(stored);
  }, [fromUrl]);

  const selectJourney = useCallback(
    (nextJourney: StockholmJourney) => {
      setJourney(nextJourney);
      persistJourney(nextJourney);
      trackStockholmEvent("journey_select", {
        journey: nextJourney,
        source_surface: "homepage",
      });
    },
    [trackStockholmEvent],
  );

  const hrefFor = useCallback(
    (path: string) => {
      if (!journey) return path;
      const url = new URL(path, "https://hemtjanstistockholm.se");
      url.searchParams.set("resa", journey);
      return `${url.pathname}${url.search}${url.hash}`;
    },
    [journey],
  );

  const value = useMemo(
    () => ({ journey, selectJourney, hrefFor }),
    [hrefFor, journey, selectJourney],
  );

  return (
    <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>
  );
}

export function useJourney(): JourneyContextValue {
  const context = useContext(JourneyContext);
  if (!context)
    throw new Error("useJourney must be used inside JourneyProvider");
  return context;
}
